import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { searchCases } from '../../../services/clienteService';
import { generarKardexCliente } from '../../../services/reporteService';
import { ClienteCaso } from '../../../interfaces/cliente.types';
import styles from './ReportCard.module.css';
import { FileText, Search, Download, AlertCircle, X } from 'lucide-react';

export const KardexReportCard: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<ClienteCaso[]>([]);
    const [selectedCase, setSelectedCase] = useState<ClienteCaso | null>(null);
    const [isSearching, setIsSearching] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSearch = async (e: React.FormEvent) => {
        e.preventDefault();
        if (searchTerm.length < 3) return;

        setIsSearching(true);
        setError(null);
        try {
            const results = await searchCases(searchTerm);
            setSearchResults(results);
        } catch (err: any) {
            setError(err.message || "Error al buscar casos.");
        } finally {
            setIsSearching(false);
        }
    };
    
    const handleSelectCase = (caso: ClienteCaso) => {
        setSelectedCase(caso);
        setSearchTerm(caso.nombre_completo);
        setSearchResults([]);
    };

    const handleGenerateReport = async () => {
        if (!selectedCase) {
            setError("Por favor, seleccione un caso para generar el reporte.");
            return;
        }
        
        setIsLoading(true);
        setError(null);
        try {
            await generarKardexCliente(selectedCase.id, selectedCase.nombre_completo);
        } catch (err: any) {
            setError(err.message || "No se pudo generar el reporte.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <motion.div 
            className={styles.reportCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: 'easeOut', delay: 0.1 }}
        >
            <div className={styles.cardIconWrapper} style={{ backgroundColor: '#e0f2fe', color: '#0284c7' }}>
                <FileText size={28} />
            </div>
            <h3 className={styles.cardTitle}>Kardex Detallado por Cliente</h3>
            <p className={styles.cardDescription}>
                Busque y seleccione un expediente específico para descargar un reporte completo en Excel con todos sus detalles.
            </p>

            <form onSubmit={handleSearch} className={styles.kardexSearchWrapper}>
                <div className={styles.searchInputGroup}>
                    <Search size={18} />
                    <input 
                        type="text" 
                        placeholder="Buscar cliente, DNI o expediente..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setSelectedCase(null);
                        }}
                    />
                    {isSearching && <div className={styles.spinner}></div>}
                </div>
                <AnimatePresence>
                    {searchResults.length > 0 && (
                        <motion.ul 
                            className={styles.searchResults}
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                        >
                            {searchResults.map(caso => (
                                <li key={caso.id} onClick={() => handleSelectCase(caso)}>
                                    <span className={styles.resultName}>{caso.nombre_completo}</span>
                                    <span className={styles.resultExp}>Exp: {caso.numero_expediente || 'N/A'}</span>
                                </li>
                            ))}
                        </motion.ul>
                    )}
                </AnimatePresence>
            </form>
            
            {error && 
                <div className={styles.errorMessage}>
                    <AlertCircle size={18} />
                    <span>{error}</span>
                </div>
            }

            <button 
                className={styles.generateButton} 
                onClick={handleGenerateReport} 
                disabled={isLoading || !selectedCase}
            >
                {isLoading ? (
                    'Generando...'
                ) : (
                    <>
                        <Download size={18} />
                        <span>Generar Kardex del Cliente</span>
                    </>
                )}
            </button>
        </motion.div>
    );
};